import { StepsWizard, TStep, TStepContent, TStepHeader } from '@verifime/components';
import { useEffect, useState } from 'react';
import { IdentityDocumentWizardStep } from './IdentityDocumentWizardStep';
import { TIdentityDocumentsUploadConfig } from './types';
import { entityDocumentApi, identityUtils } from './utils';
import { WizardProvider, useWizardContext } from './WizardContext';

type TStepOperations = {
  onFinish?: () => void;
  onCancel?: () => void;
  dataTestId: string;
};

const generateStep = ({ dataTestId, onFinish, onCancel }: TStepOperations) => {
  return {
    content: (stepInfo: TStepContent) => {
      return (
        <IdentityDocumentWizardStep
          key={`identityDocumentsWizardStep${stepInfo.stepIndex + 1}`}
          onFinish={onFinish}
          onCancel={onCancel}
          API={{
            putIdentityDocumentTypeSelections: entityDocumentApi.putIdentityDocumentTypeSelections,
            putIdentityDocument: entityDocumentApi.putIdentityDocument,
            scanIdentityDocument: entityDocumentApi.scanIdentityDocument,
          }}
          {...stepInfo}
        />
      );
    },
  };
};

const getIdentityDocumentsUploadFullSteps = ({
  onFinish,
  onCancel,
  dataTestId,
  uploadIdDocumentConfig,
}: TStepOperations & { uploadIdDocumentConfig: TIdentityDocumentsUploadConfig }) => {
  if (!uploadIdDocumentConfig) {
    throw new Error('Configuration is required for IdentityDocumentsUpload');
  }

  if (
    !uploadIdDocumentConfig.documentTypeChoices ||
    !Array.isArray(uploadIdDocumentConfig.documentTypeChoices) ||
    uploadIdDocumentConfig.documentTypeChoices.length === 0
  ) {
    console.error('documentTypeChoices is required and must be a non-empty array');
    return [];
  }

  if (!uploadIdDocumentConfig.documentCount) {
    console.error('documentCount is required and must be a number greater than 0');
    return [];
  }

  if (uploadIdDocumentConfig.documentCount > uploadIdDocumentConfig.documentTypeChoices.length) {
    console.error('documentCount must be less than or equal to documentTypeChoices length');
    return [];
  }

  const steps: TStep[] = [];
  for (let i = 1; i <= uploadIdDocumentConfig.documentCount; i++) {
    const isFirstStep = i === 1;
    const isLastStep = i === uploadIdDocumentConfig.documentCount;
    steps.push(
      generateStep({
        onFinish: isLastStep ? onFinish : undefined,
        onCancel: isFirstStep ? onCancel : undefined,
        dataTestId,
      }),
    );
  }
  return steps;
};

const IdentityDocumentsWizardContent = ({
  header,
  onFinish,
  onCancel,
  dataTestId,
  isShowStepConnector = false,
}: {
  header?: TStepHeader;
  isShowStepConnector?: boolean;
} & TStepOperations) => {
  const { state } = useWizardContext();

  if (!state.uploadIdDocumentConfig) {
    throw new Error('Configuration is required for IdentityDocumentsUpload');
  }

  const steps = getIdentityDocumentsUploadFullSteps({
    onFinish,
    onCancel,
    dataTestId,
    uploadIdDocumentConfig: state.uploadIdDocumentConfig,
  });

  return (
    <StepsWizard
      dataTestId={dataTestId}
      isShowStepConnector={isShowStepConnector}
      steps={steps}
      header={header}
    />
  );
};

export default function IdentityDocumentsWizard({
  customerId,
  countryCode,
  header,
  onFinish,
  onCancel,
  maxRetry,
  pollingIntervalInMs,
  dataTestId,
  isShowStepConnector = false,
}: {
  customerId: string;
  countryCode: string;
  header?: TStepHeader;
  maxRetry?: number;
  pollingIntervalInMs?: number;
  isShowStepConnector?: boolean;
} & TStepOperations) {
  const [config, setConfig] = useState<TIdentityDocumentsUploadConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch identity document requirements based on the current country.
    setIsLoading(true);
    identityUtils
      .getIdentityDocumentsUploadConfig({ maxRetry, pollingIntervalInMs })
      .then(setConfig)
      .catch((error) => {
        console.error('Failed to load identity document config:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // countryCode is included in deps because the API returns country-specific document requirements
    // based on the user's profile. When user changes their country and returns to this step,
    // we need to refetch the config to get the correct document options for their new country.
  }, [countryCode, maxRetry, pollingIntervalInMs]);

  if (!customerId || !countryCode) {
    console.error('customerId and countryCode are required for IdentityDocumentsWizard');
    return null;
  }

  if (isLoading || !config) {
    return null;
  }

  return (
    <WizardProvider initialConfig={config} customerId={customerId} countryCode={countryCode}>
      <IdentityDocumentsWizardContent
        header={header}
        onFinish={onFinish}
        onCancel={onCancel}
        dataTestId={dataTestId}
        isShowStepConnector={isShowStepConnector}
      />
    </WizardProvider>
  );
}
